import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Tooltip, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import messages from "../../../assets/locale/messages";
import Upload from "../../../components/Upload";
import Input from "../../../components/Input";
import Button from "../../../components/Button";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  BLOB_CONTAINERS,
  UploadBlobToContainer
} from "../../../utils/blobStorageServices";
import {
  addCategoryRequest,
  editCategoryRequest,
  getCategoryDetailsRequest,
  getCategoryDetailsResponse,
  getCategoryRequestDetailsRequest,
  getCategoryRequestDetailsResponse
} from "../../../store/Categories/actions";
import { checkBackgroundImageValidity } from "../../../utils/Helpers";
import "./AddEditCategory.scss";

const AddEditCategory = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const uploadElementRef = useRef();

  const lang = useSelector((state) => state.locale.lang);
  const categoryDetails = useSelector(
    (state) => state.categories.categoryDetails
  );

  const categoryRequestDetails = useSelector(
    (state) => state.categories.categoryRequestDetails
  );

  const [isValidWebsiteCoverImage, setIsWebsiteCoverImage] =
    useState(false);
  const [isValidAdminCoverImage, setIsValidAdminCoverImage] =
    useState(false);

  useEffect(() => {
    if (id) {
      if (!!location?.state?.isRequest) {
        dispatch(getCategoryRequestDetailsRequest({ id }));
      } else {
        dispatch(
          getCategoryDetailsRequest({
            id
          })
        );
      }
    }
  }, [id]);

  useEffect(() => {
    return () => {
      if (!!location?.state?.isRequest) {
        dispatch(getCategoryRequestDetailsResponse({}));
      } else {
        dispatch(getCategoryDetailsResponse({}));
      }
    };
  }, []);

  const {
    shared,
    categories: {
      addEditCategory: {
        addPageTitle,
        editPageTitle,
        categoryDetailsTitle,
        websiteCoverImagePlaceholder,
        adminCoverImagePlaceholder,
        inputLabels: { categoryName, categoryDescription },
        placeholders: {
          categoryNamePlaceholder,
          categoryDescriptionPlaceholder
        }
      }
    },
    inputsValidations
  } = messages[lang];

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    dirty,
    isValid,
    setValues
  } = useFormik({
    initialValues: {
      categoryName: "",
      categoryDescription: "",
      adminListCoverImage: null,
      adminListCoverImageInfo: {},
      categoryListCoverImage: null,
      categoryListCoverImageInfo: {}
    },
    validationSchema: Yup.object({
      categoryName: Yup.string()
        .max(50, "categoryNameLong")
        .required("CategoryNameRequired"),
      categoryDescription: Yup.string()
        .max(500, "categoryDescriptionLong")
        .required("CategoryDescriptionRequired"),
      categoryListCoverImage: Yup.mixed().required(
        "categoryListCoverImageRequired"
      ),
      adminListCoverImage: Yup.mixed().required(
        "adminListCoverImageRequired"
      )
    }),
    onSubmit: async ({
      categoryName,
      categoryDescription,
      categoryListCoverImage,
      dbCategoryListCoverImage,
      adminListCoverImage,
      dbAdminListCoverImage,
      categoryListCoverImageInfo,
      adminListCoverImageInfo
    }) => {
      let coverImgName = "";
      let adminImgName = "";

      if (categoryListCoverImage) {
        if (categoryListCoverImageInfo.isNew) {
          coverImgName = await UploadBlobToContainer(
            {
              blob: categoryListCoverImage,
              ...categoryListCoverImageInfo
            },
            BLOB_CONTAINERS.images
          );
        } else {
          coverImgName = categoryListCoverImage.split("/images/")[1];
        }
      }

      if (adminListCoverImage) {
        if (adminListCoverImageInfo.isNew) {
          adminImgName = await UploadBlobToContainer(
            {
              blob: adminListCoverImage,
              ...adminListCoverImageInfo
            },
            BLOB_CONTAINERS.images
          );
        } else {
          adminImgName = adminListCoverImage.split("/images/")[1];
        }
      }
      const categoryData = {
        Id: 0,
        Name: categoryName.trim(),
        Description: categoryDescription.trim(),
        AdminCoverImage: adminImgName,
        OriginalAdminCoverImageName: adminListCoverImageInfo.name,
        WebsiteCoverImage: coverImgName,
        OriginalWebsiteCoverImageName: categoryListCoverImageInfo.name
      };
      const dbData = {
        dbAdminListCoverImage,
        dbCategoryListCoverImage,
        adminCoverImageIsNew: adminListCoverImageInfo.isNew,
        categoryListCoverImageIsNew: categoryListCoverImageInfo.isNew
      };

      if (id) {
        categoryData.Id = id;
        dispatch(
          editCategoryRequest({
            data: categoryData,
            dbData,
            navigate,
            id: categoryData.Id,
            isRequest: !!location?.state?.isRequest
          })
        );
      } else {
        dispatch(
          addCategoryRequest({
            data: categoryData,
            navigate
          })
        );
      }
    }
  });

  useEffect(() => {
    if (categoryDetails && Object.keys(categoryDetails).length) {
      const {
        Name,
        Description,
        AdminCoverImage,
        OriginalAdminCoverImageName,
        WebsiteCoverImage,
        OriginalWebsiteCoverImageName
      } = categoryDetails;
      setValues({
        ...values,
        categoryName: Name,
        categoryDescription: Description,
        adminListCoverImage: AdminCoverImage,
        dbAdminListCoverImage: AdminCoverImage,
        categoryListCoverImage: WebsiteCoverImage,
        dbCategoryListCoverImage: WebsiteCoverImage,
        categoryListCoverImageInfo: {
          isNew: false,
          name: OriginalAdminCoverImageName
        },
        adminListCoverImageInfo: {
          isNew: false,
          name: OriginalWebsiteCoverImageName
        }
      });
    }
    if (
      categoryRequestDetails &&
      Object.keys(categoryRequestDetails).length
    ) {
      const {
        Name,
        Description,
        AdminCoverImage,
        OriginalAdminCoverImageName,
        WebsiteCoverImage,
        OriginalWebsiteCoverImageName
      } = categoryRequestDetails;

      checkBackgroundImageValidity(AdminCoverImage, (isValid) => {
        setIsValidAdminCoverImage(isValid);
      });

      checkBackgroundImageValidity(WebsiteCoverImage, (isValid) => {
        setIsWebsiteCoverImage(isValid);
      });

      setValues({
        ...values,
        categoryName: Name,
        categoryDescription: Description,
        adminListCoverImage: isValidAdminCoverImage
          ? AdminCoverImage
          : null,
        dbAdminListCoverImage: isValidAdminCoverImage
          ? AdminCoverImage
          : null,
        categoryListCoverImage: isValidWebsiteCoverImage
          ? WebsiteCoverImage
          : null,
        dbCategoryListCoverImage: isValidWebsiteCoverImage
          ? WebsiteCoverImage
          : null,
        categoryListCoverImageInfo: {
          isNew: false,
          name: OriginalAdminCoverImageName
        },
        adminListCoverImageInfo: {
          isNew: false,
          name: OriginalWebsiteCoverImageName
        }
      });
    }
  }, [
    categoryDetails,
    categoryRequestDetails,
    isValidAdminCoverImage,
    isValidWebsiteCoverImage
  ]);

  return (
    <div className="add-edit-category-container mb-4 d-flex flex-column">
      <div className="page-title ">
        <h3 className="fsize-22 fweight-700 title-color mb-4">
          {id ? editPageTitle : addPageTitle}
        </h3>
      </div>
      <div>
        <form
          noValidate
          onSubmit={handleSubmit}
          className="add-edit-product-form-container d-flex flex-column">
          <div className="form-data bg-white p-4 pb-5 mb-4 border-radius-10">
            <h5 className="fsize-16 fweight-600 title-color mb-3">
              {categoryDetailsTitle}
            </h5>
            <div
              className={"category-list-cover-container my-3 mb-5"}>
              <div className="block-title d-flex gap-2 align-items-md-center align-items-start pb-3">
                <Typography
                  variant="span"
                  className="text-light-grey website-cover-image-title">
                  {websiteCoverImagePlaceholder}
                </Typography>
                <Tooltip
                  className="text-light-grey ps-md-0 m-md-0 "
                  title={
                    <ul className="mb-0 ps-0">
                      <li>{inputsValidations.uploadSizeError}</li>
                      <li>{inputsValidations.uploadImgSizeError}</li>
                      <li>{inputsValidations.uploadTypeError}</li>
                    </ul>
                  }>
                  <InfoOutlined />
                </Tooltip>
              </div>
              <div className="cover-img-only-container">
                <div
                  className={`category-list-cover-img-container mb-md-4 mb-3 col-12`}>
                  <Upload
                    showValidationMsg
                    required
                    label={<div ref={uploadElementRef}></div>}
                    onChange={(img) => {
                      setFieldTouched("categoryListCoverImage");
                      setValues({
                        ...values,
                        categoryListCoverImage: img.blob,
                        categoryListCoverImageInfo: {
                          ...img?.imageInfo,
                          isNew: true
                        }
                      });
                    }}
                    userImage={values.categoryListCoverImage}
                    onClear={() => {
                      setFieldTouched("categoryListCoverImage");
                      setValues({
                        ...values,
                        categoryListCoverImage: null,
                        categoryListCoverImageInfo: {}
                      });
                    }}
                    name="categoryListCoverImage"
                    isInputHasErr={
                      !!(
                        touched.categoryListCoverImage &&
                        errors.categoryListCoverImage
                      )
                    }
                    errMsg={errors.categoryListCoverImage}
                    isCropMode={true}
                    cropAspect={8.25 / 1}
                  />
                </div>
              </div>
            </div>
            <div className="admin-cover-container pb-md-3 my-md-0 my-3">
              <div className="d-flex gap-2 pb-md-3 pb-1">
                <Typography
                  variant="span"
                  className="text-light-grey image-title">
                  {adminCoverImagePlaceholder}
                </Typography>
                <Tooltip
                  className="text-light-grey"
                  title={
                    <ul className="mb-0 ps-0">
                      <li>{inputsValidations.uploadSizeError}</li>
                      <li>{inputsValidations.uploadImgSizeError}</li>
                      <li>{inputsValidations.uploadTypeError}</li>
                    </ul>
                  }>
                  <InfoOutlined />
                </Tooltip>
              </div>
              <div className={`cover-img-data-container col-12 `}>
                <div
                  className={`admin-list-cover-img-container col-xl-4 col-lg-6 col-md-6 col-12 my-3 my-md-0 `}>
                  <Upload
                    showValidationMsg
                    required
                    label={<div ref={uploadElementRef}></div>}
                    onChange={(img) => {
                      setFieldTouched("adminListCoverImage");
                      setValues({
                        ...values,
                        adminListCoverImage: img.blob,
                        adminListCoverImageInfo: {
                          ...img?.imageInfo,
                          isNew: true
                        }
                      });
                    }}
                    userImage={values.adminListCoverImage}
                    onClear={() => {
                      setFieldTouched("adminListCoverImage");
                      setValues({
                        ...values,
                        adminListCoverImage: null,
                        adminListCoverImageInfo: {}
                      });
                    }}
                    name="adminListCoverImage"
                    isInputHasErr={
                      !!(
                        touched.adminListCoverImage &&
                        errors.adminListCoverImage
                      )
                    }
                    errMsg={errors.adminListCoverImage}
                    isCropMode={true}
                    cropAspect={1 / 1}
                  />
                </div>
                <div className="data-container d-flex flex-column col-xl-7 col-lg-6 col-md-6 col-12">
                  <div
                    className={`product-inputs-container d-flex flex-column gap-4 justify-content-between flex-wrap`}>
                    <Input
                      required
                      key={categoryName}
                      label={categoryName}
                      labelClassName="pb-2 font-medium  main-text-color"
                      placeholder={categoryNamePlaceholder}
                      helperTextClass="fweight-300 fsize-12 mt-1"
                      onChange={(value) => {
                        if (value?.trim() || !value) {
                          const modifiedValue = value.replace(
                            /\s\s+/g,
                            " "
                          );
                          if (value[0] !== " ") {
                            setFieldTouched("categoryName");
                            setFieldValue(
                              "categoryName",
                              modifiedValue
                            );
                          }
                        }
                      }}
                      value={values.categoryName}
                      isInputHasErr={
                        !!(
                          touched.categoryName && errors.categoryName
                        )
                      }
                      errMsg={errors.categoryName}
                      name="categoryName"
                      id="categoryName"
                    />
                    <Input
                      required
                      key={categoryDescription}
                      label={categoryDescription}
                      placeholder={categoryDescriptionPlaceholder}
                      labelClassName="pb-2 font-medium   main-text-color"
                      helperTextClass="fweight-300 fsize-13 "
                      rows={7}
                      multiline
                      onChange={(value) => {
                        if (value?.trim() || !value) {
                          const modifiedValue = value.replace(
                            /\s\s+/g,
                            " "
                          );
                          if (value[0] !== " ") {
                            setFieldTouched("categoryDescription");
                            setFieldValue(
                              "categoryDescription",
                              modifiedValue
                            );
                          }
                        }
                      }}
                      value={values.categoryDescription}
                      isInputHasErr={
                        !!(
                          touched.categoryDescription &&
                          errors.categoryDescription
                        )
                      }
                      errMsg={errors.categoryDescription}
                      name="categoryDescription"
                      id="categoryDescription"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`form-buttons-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8`}>
            <div
              className={`product-inputs-container d-flex  justify-content-between `}>
              <Button
                key={shared.cancel}
                outlined
                className={"w-auto px-5"}
                label={shared.cancel}
                onClick={() =>
                  navigate(ROUTE_PATHS.approvedCategories)
                }
              />
              <Button
                key={shared.submit}
                type="submit"
                className={"w-auto px-5"}
                disabled={!dirty || !isValid}
                label={id ? shared.save : shared.submit}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCategory;
